/**
 * An email draft an agent posted (`email_draft` → `data.email` on its chat message), and
 * the ways it leaves the app: rich HTML for the clipboard, plain text, a `mailto:` link
 * and an Outlook web compose link. Nothing here, or anywhere in the app, sends one.
 *
 * The HTML is written for a mail client, not for this page: every style inline, black
 * text in a common mail font, no background at all. Copying the chat's own rendering
 * took the dark theme's computed colours with it, which is what pasted into Outlook as
 * a black box.
 */
import type { AgentMessage } from "@/lib/types";

export interface EmailFields {
  to: string[];
  cc: string[];
  subject: string;
  /** Markdown. */
  body: string;
  inReplyTo: string | null;
}

export interface EmailDraft extends EmailFields {
  revisions: Array<EmailFields & { savedBy: string; savedAt: string }>;
  revisionOf: string | null;
  supersededBy: string | null;
}

export function emailOf(message: AgentMessage): EmailDraft | null {
  const email = message.data?.email as EmailDraft | undefined;
  if (!email || typeof email !== "object" || typeof email.subject !== "string" || typeof email.body !== "string") return null;
  return {
    to: Array.isArray(email.to) ? email.to : [],
    cc: Array.isArray(email.cc) ? email.cc : [],
    subject: email.subject,
    body: email.body,
    inReplyTo: email.inReplyTo ?? null,
    revisions: Array.isArray(email.revisions) ? email.revisions : [],
    revisionOf: email.revisionOf ?? null,
    supersededBy: email.supersededBy ?? null,
  };
}

/** "Sam Example <sam@example.com>" → "sam@example.com"; a bare address as it is. */
export function addressOf(recipient: string): string {
  const match = /<([^<>]+)>\s*$/.exec(recipient);
  return (match ? match[1]! : recipient).trim();
}

/** "a@x.com, B <b@y.com>; c@z" → three recipients. */
export const splitRecipients = (text: string): string[] =>
  text
    .split(/[;,\n]/)
    .map((part) => part.trim())
    .filter(Boolean);

/* ------------------------------------------------------------ markdown → mail */

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const FONT = "font-family:Aptos,Calibri,Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.4;color:black";
const P = `margin:0 0 10pt 0;${FONT}`;
const LIST = `margin:0 0 10pt 0;padding-left:24pt;${FONT}`;
const QUOTE = `margin:0 0 10pt 0;padding-left:10pt;border-left:3px solid silver;color:dimgray;${FONT.replace("color:black", "")}`;

/** Bold, italic, code and links inside one line of text, escaped. Also used for plain text. */
function inline(text: string, html: boolean): string {
  const tokens: string[] = [];
  const keep = (value: string) => `\u0000${tokens.push(value) - 1}\u0000`;
  let out = text;
  out = out.replace(/`([^`]+)`/g, (_, code: string) => keep(html ? `<span style="font-family:Consolas,monospace">${escapeHtml(code)}</span>` : code));
  out = out.replace(/\[([^\]]+)\]\(((?:https?:|mailto:)[^)\s]+)\)/g, (_, label: string, url: string) =>
    keep(html ? `<a href="${escapeHtml(url)}" style="color:blue">${escapeHtml(label)}</a>` : `${label} (${url})`),
  );
  if (html) out = escapeHtml(out);
  out = out.replace(/\*\*(?=\S)(.+?)(?<=\S)\*\*|__(?=\S)(.+?)(?<=\S)__/g, (_, a?: string, b?: string) => (html ? `<b>${a ?? b}</b>` : (a ?? b)!));
  out = out.replace(/(^|[^\w*])\*(?=\S)([^*]+?)(?<=\S)\*(?!\w)|(^|[^\w])_(?=\S)([^_]+?)(?<=\S)_(?!\w)/g, (_, p1?: string, a?: string, p2?: string, b?: string) =>
    `${p1 ?? p2 ?? ""}${html ? `<i>${a ?? b}</i>` : (a ?? b)}`,
  );
  if (html) out = out.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, (_, pre: string, url: string) => `${pre}<a href="${url}" style="color:blue">${url}</a>`);
  return out.replace(/\u0000(\d+)\u0000/g, (_, index: string) => tokens[Number(index)]!);
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul" | "ol"; items: Array<{ text: string; depth: number }> }
  | { kind: "quote"; lines: string[] };

function blocks(markdown: string): Block[] {
  const out: Block[] = [];
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  let current: Block | null = null;
  const flush = () => {
    if (current) out.push(current);
    current = null;
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flush();
      continue;
    }
    const bullet = /^(\s*)[-*+]\s+(.*)$/.exec(line);
    const numbered = /^(\s*)\d+[.)]\s+(.*)$/.exec(line);
    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (bullet || numbered) {
      const kind = bullet ? "ul" : "ol";
      const [, indent, text] = (bullet ?? numbered)!;
      const depth = Math.min(Math.floor(indent!.replace(/\t/g, "  ").length / 2), 3);
      // A nested item of the other kind stays in the list it sits under.
      if (!(current && (current.kind === "ul" || current.kind === "ol") && (current.kind === kind || depth > 0))) {
        flush();
        current = { kind, items: [] };
      }
      (current as Extract<Block, { kind: "ul" | "ol" }>).items.push({ text: text!, depth });
      continue;
    }
    if (quote) {
      if (current?.kind !== "quote") {
        flush();
        current = { kind: "quote", lines: [] };
      }
      current.lines.push(quote[1]!);
      continue;
    }
    // A heading is a bold line in a mail; a rule is nothing.
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flush();
      continue;
    }
    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line);
    const text = heading ? `**${heading[1]}**` : line.trim();
    if (current && (current.kind === "ul" || current.kind === "ol") && /^\s{2,}/.test(line)) {
      current.items[current.items.length - 1]!.text += ` ${line.trim()}`;
      continue;
    }
    if (current?.kind !== "p") {
      flush();
      current = { kind: "p", lines: [] };
    }
    current.lines.push(text);
  }
  flush();
  return out;
}

function listHtml(kind: "ul" | "ol", items: Array<{ text: string; depth: number }>): string {
  let html = "";
  let depth = -1;
  for (const item of items) {
    while (depth < item.depth) {
      html += depth < 0 ? `<${kind} style="${LIST}">` : `<ul style="margin:0;padding-left:20pt">`;
      depth++;
    }
    while (depth > item.depth) {
      html += "</ul>";
      depth--;
    }
    html += `<li style="margin:0 0 2pt 0">${inline(item.text, true)}</li>`;
  }
  while (depth > 0) {
    html += "</ul>";
    depth--;
  }
  return `${html}</${kind}>`;
}

/** The body as a mail client wants it: inline styles, black text, no background. */
export function emailBodyHtml(markdown: string): string {
  const html = blocks(markdown)
    .map((block) => {
      if (block.kind === "p") return `<p style="${P}">${block.lines.map((line) => inline(line, true)).join("<br>")}</p>`;
      if (block.kind === "quote") return `<blockquote style="${QUOTE}">${block.lines.map((line) => inline(line, true)).join("<br>")}</blockquote>`;
      return listHtml(block.kind, block.items);
    })
    .join("");
  return `<div style="${FONT}">${html}</div>`;
}

/** The body as plain text: what `text/plain`, `mailto:` and the Outlook link carry. */
export function emailBodyText(markdown: string): string {
  return blocks(markdown)
    .map((block) => {
      if (block.kind === "p") return block.lines.map((line) => inline(line, false)).join("\n");
      if (block.kind === "quote") return block.lines.map((line) => `> ${inline(line, false)}`).join("\n");
      let n = 0;
      return block.items
        .map((item) => `${"   ".repeat(item.depth)}${block.kind === "ol" && item.depth === 0 ? `${++n}.` : "•"} ${inline(item.text, false)}`)
        .join("\n");
    })
    .join("\n\n");
}

/* ----------------------------------------------------------------- getting out */

/** mailto: links are cut short by some clients well before this; past it the body is left out. */
export const MAILTO_MAX = 1_900;
/** Outlook web takes a long URL, but not an unbounded one. */
export const OUTLOOK_MAX = 7_500;

export function mailtoLink(fields: EmailFields, withBody = true): string {
  const params = [
    fields.cc.length ? `cc=${encodeURIComponent(fields.cc.map(addressOf).join(","))}` : null,
    `subject=${encodeURIComponent(fields.subject)}`,
    withBody ? `body=${encodeURIComponent(emailBodyText(fields.body))}` : null,
  ].filter(Boolean);
  return `mailto:${fields.to.map((to) => encodeURIComponent(addressOf(to))).join(",")}?${params.join("&")}`;
}

export function outlookLink(fields: EmailFields, withBody = true): string {
  const query = new URLSearchParams();
  if (fields.to.length) query.set("to", fields.to.map(addressOf).join(";"));
  if (fields.cc.length) query.set("cc", fields.cc.map(addressOf).join(";"));
  query.set("subject", fields.subject);
  if (withBody) query.set("body", emailBodyText(fields.body));
  return `https://outlook.office.com/mail/deeplink/compose?${query.toString().replace(/\+/g, "%20")}`;
}

/** The link to open, and whether the body had to stay out of it (copy it instead). */
export function openLink(kind: "outlook" | "mailto", fields: EmailFields): { url: string; bodyLeftOut: boolean } {
  const build = kind === "outlook" ? outlookLink : mailtoLink;
  const max = kind === "outlook" ? OUTLOOK_MAX : MAILTO_MAX;
  const full = build(fields, true);
  return full.length <= max ? { url: full, bodyLeftOut: false } : { url: build(fields, false), bodyLeftOut: true };
}

/** Writes rich HTML and plain text to the clipboard at once, so a paste keeps formatting wherever it can. */
export async function copyRich(html: string, text: string): Promise<void> {
  const document_ = `<html><body>${html}</body></html>`;
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": new Blob([document_], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) }),
      ]);
      return;
    } catch {
      // Refused (an old Safari, a permission): the copy event below still works.
    }
  }
  const onCopy = (event: ClipboardEvent) => {
    event.preventDefault();
    event.clipboardData?.setData("text/html", document_);
    event.clipboardData?.setData("text/plain", text);
  };
  document.addEventListener("copy", onCopy, { once: true });
  const ok = document.execCommand("copy");
  document.removeEventListener("copy", onCopy);
  if (!ok) await navigator.clipboard.writeText(text);
}

/* ------------------------------------------------- copying anything from a chat */

const KEEP_TAGS = new Set(["P", "BR", "B", "STRONG", "I", "EM", "U", "A", "UL", "OL", "LI", "BLOCKQUOTE", "CODE", "PRE", "TABLE", "THEAD", "TBODY", "TR", "TH", "TD", "H1", "H2", "H3", "H4", "DIV", "SPAN"]);

/**
 * What a selection in the chat should paste as elsewhere: its structure — paragraphs,
 * lists, bold, links, tables — with every class and style dropped and the mail font
 * put back, so the page's dark colours never travel with it.
 */
export function cleanSelectionHtml(fragment: DocumentFragment): string {
  const root = document.createElement("div");
  root.appendChild(fragment);
  root.querySelectorAll("button, svg, img, input, textarea, script, style, [aria-hidden='true']").forEach((node) => node.remove());
  const walk = (element: Element) => {
    for (const child of [...element.children]) {
      walk(child);
      if (!KEEP_TAGS.has(child.tagName)) {
        child.replaceWith(...child.childNodes);
        continue;
      }
      const href = child.tagName === "A" ? child.getAttribute("href") : null;
      for (const attribute of [...child.attributes]) child.removeAttribute(attribute.name);
      if (href && /^(https?:|mailto:)/i.test(href)) child.setAttribute("href", href);
      if (child.tagName === "P" || child.tagName === "UL" || child.tagName === "OL") child.setAttribute("style", child.tagName === "P" ? P : LIST);
      if (child.tagName === "TD" || child.tagName === "TH") child.setAttribute("style", "border:1px solid silver;padding:3pt 6pt");
    }
  };
  walk(root);
  return `<div style="${FONT}">${root.innerHTML}</div>`;
}
